<template>
  <ReportCard :title="$t('report.members_conversion_title')">
    <template #header>
      <button @click.stop="showActivityInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="relative">
      <div ref="chartEl" class="w-full" style="height: 280px"></div>
      <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-card/60">
        <Loading />
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showActivityInfo" variant="sidebar">
    <MembersActivityInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import type { MembersStatsResponse } from '../../responses'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import { colorsUtils } from '../../utils/ColorsUtils'
import BottomSheet from '../BottomSheet.vue'
import MembersActivityInfoSheet from '../MembersActivityInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: MembersStatsResponse | null
  loading: boolean
}>()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const showActivityInfo = ref(false)

const isDark = useIsDarkMode()

function buildChart() {
  if (!chartEl.value || !props.response?.data) return

  if (!chartInstance.value) {
    chartInstance.value = echarts.init(chartEl.value)
  }

  const dark = isDark.value

  const data = props.response.data
    .filter(d => d.dimensions.first_activity !== 'none')
    .sort((a, b) => (a.metrics.count ?? 0) - (b.metrics.count ?? 0))

  const total = data.reduce((s, d) => s + (d.metrics.count ?? 0), 0)

  const categories = data.map(d => d.dimensions.first_activity)
  const values = data.map(d => d.metrics.count ?? 0)

  chartInstance.value.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: dark ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1, borderRadius: 8, confine: true,
      textStyle: { color: dark ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: any) => {
        const p = params[0]
        const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : '0'
        return `<b>${p.name}</b><br/>${p.value.toLocaleString()} (${pct}%)`
      },
    },
    grid: { left: '3%', right: '14%', bottom: '4%', top: '4%', containLabel: true },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.06)' : 'rgba(229,231,235,0.3)', type: 'dashed' } },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.7)', fontSize: 9 },
    },
    yAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(156,163,175,0.25)' } },
      axisTick: { show: false },
      axisLabel: { color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 10 },
    },
    series: [{
      type: 'bar',
      data: values.map((v, i) => ({
        value: v,
        itemStyle: { color: colorsUtils.getColorForName(categories[i]), borderRadius: [0, 3, 3, 0] },
      })),
      label: {
        show: true,
        position: 'right',
        fontSize: 10,
        fontWeight: 'bold',
        color: dark ? 'rgba(255,255,255,0.7)' : 'rgba(75,85,99,0.8)',
        formatter: (p: any) => {
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(0) : '0'
          return `${p.value.toLocaleString()} (${pct}%)`
        },
      },
      barMaxWidth: 28,
    }],
  }, true)
}

function refreshChart() {
  buildChart()
}

watch(() => props.response, async () => {
  await nextTick()
  buildChart()
})

watch(isDark, () => buildChart())

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(buildChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
